/**
 * How many imagers a panoramic body fans its span across.
 *
 * WHY THIS IS ITS OWN INDEX
 * -------------------------
 * A panoramic camera is N flat imagers side by side, and that N is a coverage
 * input: each imager's near edge curves away at its own flanks, so where two
 * imagers meet there is a wedge of ground neither one reaches. Model the body
 * as ONE frustum and that wedge becomes a single huge "U"; model it as one
 * declination swept around the vertical and the wedge disappears into a
 * perfect circle. Both were shipped and both were rejected on 2026-08-13.
 * Only the real N puts the notches where an installer will find them.
 *
 * It deliberately does NOT live in `multiSensorIndex.ts`. That index means
 * "fan this SKU into N independently aimable heads", and its `channelCount`
 * feeds the BOM's channel maths. A stitched panorama is one aimable device
 * with one picture, so borrowing that record would fan it into heads it does
 * not have and could change NVR channel counts as a side effect.
 *
 * PROVENANCE IS PART OF THE RECORD
 * --------------------------------
 * `basis` is not decoration. Manufacturer pages very often publish the size of
 * ONE imager ("Image sensor size: 1/2.9\"") and never state how many there
 * are, so a reader, human or model, slides from that into "one sensor". The
 * AXIS P3807-PVE support page does exactly this. An entry may therefore only
 * claim `datasheet` when the source states the COUNT in words; anything
 * reconstructed from the stitched resolution is `resolution` and says so in
 * the interface the installer sees.
 *
 * Bodies with no entry fall back to `defaultImagerCount` in geometry/
 * projection.ts, which is labelled derived in the UI as well. The gap is
 * listed by `npx tsx scripts/audit-panoramic-imagers.mjs`, so an empty entry
 * is a visible to-do rather than a silent guess.
 *
 * ADDING AN ENTRY
 * ---------------
 * Quote the source. `datasheet` needs a public URL whose text states the
 * count. `resolution` needs the factorisation written out, so the next reader
 * can check the arithmetic instead of trusting it.
 */

export type ImagerCountBasis = 'datasheet' | 'focal' | 'resolution';

/**
 * `4x2.8mm` means four imagers behind 2.8 mm lenses. It is the manufacturer's
 * own notation, carried in the catalog's `focalLength` field, and it is the
 * strongest evidence we have short of a sentence on a product page.
 *
 * `2.8-12mm (4x)` is a ZOOM RATIO and means nothing about imagers, which is
 * why the multiplier is only read at the START of the string. Getting that
 * wrong would turn every varifocal into a four-sensor body.
 *
 * `fovMode.parseMinFocalMm` strips this prefix so a dual-lens strip is not
 * read as a 2 mm fisheye. Stripping it was right; throwing it away was not.
 */
const IMAGER_MULTIPLIER = /^\s*(\d+)\s*[x\u00d7]\s*\d/i;

export function imagersFromFocalLength(focalLength?: string): number | null {
  if (!focalLength) return null;
  const m = focalLength.match(IMAGER_MULTIPLIER);
  if (!m) return null;
  const n = Number(m[1]);
  // Two to eight covers every multi-imager body ever built; anything outside
  // that is a parse accident, not a camera.
  return Number.isFinite(n) && n >= 2 && n <= 8 ? n : null;
}

export interface PanoramicImagerSpec {
  /** Imagers the body fans its horizontal span across. */
  imagers: number;
  /**
   * Published VERTICAL field of the whole device, degrees.
   *
   * Present because deriving it from the image aspect is a category error for
   * this class of camera. That derivation reads the frame as one sensor behind
   * one lens; a panorama's frame is a STITCHED CANVAS whose proportions come
   * from how the stitcher lays the imagers out, not from any imager's optics.
   * Measured against published figures the derivation runs 5 to 8 degrees low
   * every time: Vivotek MS9390-HV is 68 and we computed 62.8, Dahua
   * IPC-PFW8800-A180 is 80 and we computed 72.4. Too narrow a vertical field
   * makes everything below the axis look more sharply bent than it is.
   */
  vfovDeg?: number;
  /** How the delivered frame's rows follow elevation, when footage has shown
   *  it. Absent: cylindrical (tan) rows, the preview's default. */
  rows?: 'tan' | 'linear';
  /** Footage has shown the picture turning with a rolled housing: the stitch
   *  is fixed in the body, so `Camera.rollDeg` reaches the preview. Absent: no
   *  evidence either way, and the preview stays level. */
  rollCarried?: boolean;
  /** Horizontal angle the delivered frame really spans, degrees, where footage
   *  shows it differs from the published field. Only the preview lays its
   *  columns out over it; coverage keeps the published figure. */
  frameSpanDeg?: number;
  /** How the stitch bends the rows: a column x half-widths from the centre sees
   *  its rows `rowBend·(x² − 1/3)` half-heights lower in elevation than the
   *  middle column does. Negative means the flanks show their content higher
   *  in the frame than a level cylinder would. */
  rowBend?: number;
  /** A column x half-widths out spans (1 + flankScale·x²) of the middle
   *  column's vertical angle. */
  flankScale?: number;
  /** Where the number came from. Shown to the user, not just to us. */
  basis: ImagerCountBasis;
  /** Public URL for `datasheet`; the written-out arithmetic for `resolution`. */
  source: string;
}

/**
 * Keyed by the EXACT catalog `name`, same convention as thermalSpecsIndex and
 * multiSensorIndex. Variant suffixes are part of the key: two SKUs of one
 * family can carry different imager counts.
 */
export const PANORAMIC_IMAGERS: Record<string, PanoramicImagerSpec> = {
  // AXIS P3807-PVE. The support page states "Max video resolution: 4320x1920",
  // "Horizontal field of view: 180 °" and, for the sensor, only "Image sensor
  // size: 1/2.9\"" with no count anywhere on the page (checked 2026-08-13).
  // 4320 = 4 x 1080 and the height is a full 1920, i.e. four ordinary 1920x1080
  // imagers turned portrait and placed side by side. Recorded as `resolution`
  // for that reason: the arithmetic is exact, the wording is absent.
  'AXIS P3807-PVE': {
    imagers: 4,
    basis: 'resolution',
    source: '4320x1920 = 4 x (1080x1920), i.e. four 1920x1080 imagers in portrait, from axis.com/products/axis-p3807-pve/support',
  },
};


/**
 * Family rules, matched on manufacturer + stitched resolution.
 *
 * The evidence for these is the FACTORISATION, which is a property of the
 * resolution and not of the SKU name, so a rule is the honest shape: one
 * record covers every variant of a family and states the arithmetic that
 * backs it. An exact-name entry above always wins over a rule.
 *
 * A rule only earns its place when the split is exact AND corroborated by
 * something outside the arithmetic, because a number can always be factored
 * somehow. "Duo" in the model name and the megapixel count in Arecont's are
 * that corroboration.
 */
export interface PanoramicImagerRule {
  manufacturer: string;
  resolution: string;
  imagers: number;
  /** Published vertical field of the device, degrees. See PanoramicImagerSpec. */
  vfovDeg?: number;
  /** Row mapping of the delivered frame. See PanoramicImagerSpec. */
  rows?: 'tan' | 'linear';
  /** Picture rolls with the housing. See PanoramicImagerSpec. */
  rollCarried?: boolean;
  /** Measured frame geometry. See PanoramicImagerSpec. */
  frameSpanDeg?: number;
  rowBend?: number;
  flankScale?: number;
  /** Per rule, never per array: some of these are a sentence on a product page
   *  and some are an exact factorisation of the stitched resolution, and the
   *  installer is shown which. */
  basis: ImagerCountBasis;
  source: string;
}


/**
 * Rules matched on the model NAME, for families whose count is encoded there.
 *
 * Checked before the resolution rules, because a manufacturer naming a body
 * "AllroundDual" or "12MP" is stating the configuration, while a resolution
 * only implies it.
 */
export interface PanoramicImagerNameRule {
  manufacturer: string;
  pattern: RegExp;
  imagers: number;
  basis: ImagerCountBasis;
  source: string;
}

export const PANORAMIC_IMAGER_NAME_RULES: readonly PanoramicImagerNameRule[] = [
  // Avigilon H5A/H6A multisensor. The datasheet's configuration table gives the
  // per-sensor pixel counts and the 3x / 4x totals, so the megapixels in the
  // SKU name determine the module count exactly:
  //   3 MP module 2048x1536 -> 3 x = 6144x1536 (9 MP), 4 x = 8192x1536 (12 MP)
  //   5 MP module 2592x1944 -> 3 x = 7776x1944 (15 MP), 4 x = 10368x1944 (20 MP)
  //   8 MP module 2880x2160 -> 3 x = 8640x2160 (24 MP), 4 x = 11520x2160 (32 MP)
  {
    manufacturer: 'Avigilon',
    pattern: /-(?:9|15|24)MP-/i,
    basis: 'datasheet',
    imagers: 3,
    source: 'https://www.avigilon.com/fs/documents/avigilon-h5a-mh-datasheet-en.pdf: 9/15/24 MP are the 3 x Image Sensor rows',
  },
  {
    manufacturer: 'Avigilon',
    pattern: /-(?:12|20|32)MP-/i,
    basis: 'datasheet',
    imagers: 4,
    source: 'https://www.avigilon.com/fs/documents/avigilon-h5a-mh-datasheet-en.pdf: 12/20/32 MP are the 4 x Image Sensor rows',
  },
  // Mobotix names the configuration outright. "Dual" takes two sensor modules,
  // "Mono" takes one, and the catalog resolution (3072x2048) is the figure for
  // ONE module, which is why the pair reads as a single sensor without this.
  {
    manufacturer: 'Mobotix',
    pattern: /AllroundDual|DualFlex|D16|M16A|M15A/i,
    basis: 'datasheet',
    imagers: 2,
    source: 'https://www.mobotix.com M16/M15 AllroundDual take two day/night sensor modules; 6 MP per module, up to 6144x2048 with both fitted',
  },
  {
    manufacturer: 'Mobotix',
    pattern: /AllroundMono|Mono/i,
    basis: 'datasheet',
    imagers: 1,
    source: 'https://www.mobotix.com AllroundMono is the single-module variant of the same body',
  },
];

export const PANORAMIC_IMAGER_RULES: readonly PanoramicImagerRule[] = [
  {
    // Dahua's "2x4MP TiOC Duo Splicing" platform. Two datasheets, two different
    // SKUs of it, agree word for word, and both state the sensor count in
    // WORDS rather than leaving it to be inferred from a lone sensor size:
    //   IPC-PDW3849-A180-AS-PV  "Image Sensor: Dual 1/2.7\" CMOS",
    //                           "Max. Resolution: 4096 (H) x 1860 (V)",
    //                           "Field of View: Horizontal: 180 deg; Vertical: 86 deg"
    //   IPC-PFW3849S-A180-AS-PV identical on all four lines.
    // The rule is keyed on that shared resolution, which is what the rest of
    // the family (PDW2849, PFW2849S) also reports.
    manufacturer: 'Dahua',
    resolution: '4096x1860',
    basis: 'datasheet',
    imagers: 2,
    // MEASURED on footage, not taken from the datasheet. An
    // IPC-PDW3849-A180-AS-PV on a hall's facade, 3632x1632 stream (2026-10-08),
    // calibrated without any building model: 39 world verticals, the paving
    // lattice of 115 patches projected onto the ground (19.8 cm double-T
    // blocks), and three straight edges. Rows and columns are linear and square
    // in angle at the centre, the frame spans 175 x 78.6 degrees (a sweep from
    // 168 to 182 bottoms out at 174-176 on all three measures), and the stitch
    // bends the rows and narrows the flanks by the two terms below. With them
    // the verticals fit to 1.3 degrees rms and the straight edges to 1.1 px on
    // a 1400 px frame; without them, to 2.4 degrees.
    vfovDeg: 78.6,
    rows: 'linear',
    frameSpanDeg: 175,
    rowBend: -0.0755,
    flankScale: -0.053,
    // The same calibration finds the picture rolled 6.7 degrees with the
    // housing: the stitch turns with the body.
    rollCarried: true,
    source: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/IPC-PDW3849-A180-AS-PV_datasheet_20230821.pdf : "Dual 1/2.7\" CMOS", "4096 (H) x 1860 (V)", "Horizontal: 180 deg; Vertical: 86 deg"; same four lines in IPC-PFW3849S-A180-AS-PV_datasheet_20230821.pdf',
  },
  {
    // The case that proves the count is not cosmetic: this body is ONE lens,
    // and treating it as two would invent a seam it does not have.
    manufacturer: 'Hikvision',
    resolution: '4256x1888',
    basis: 'datasheet',
    imagers: 1,
    source: 'https://assets.hikvision.com/prd/normal/all/doc/sm000107000/DS-2CD2387G3P-LIS2UY_SLRB_Datasheet_20251209.pdf : "Image Sensor 1/2.5\" Progressive Scan CMOS", "Lens Type Fixed focal lens, 2.8 mm", "horizontal FOV 182.6"',
  },
  {
    manufacturer: 'Vivotek',
    resolution: '3840x2160',
    imagers: 2,
    vfovDeg: 68,
    basis: 'datasheet',
    source: 'https://www.vivotek.com/en-US/products/network-cameras/panoramic/ms9390-hv : 8 MP as 2 x 4 MP, "unique dual-sensor design"',
  },
  {
    manufacturer: 'Hikvision',
    resolution: '3632x1632',
    imagers: 2,
    basis: 'datasheet',
    source: 'https://www.hikvision.com/en/products/IP-Products/Network-Cameras/value-series/ds-2cd1363g2p-liuf-s-l--rb-/ : dual lens, 6 MP stitched to 3632x1632',
  },
  {
    manufacturer: 'HiLook',
    resolution: '3632x1632',
    imagers: 2,
    basis: 'resolution',
    source: 'same stitched 3632x1632 output as the Hikvision DS-2CD1363G2P dual-lens body; HiLook is Hikvision own budget line',
  },
  {
    manufacturer: 'Annke',
    resolution: '3632x1632',
    imagers: 2,
    basis: 'datasheet',
    source: 'https://ca-en.annke.com/products/fcd600 : FCD600 is the dual-lens 6 MP panoramic, same 3632x1632 stitched output',
  },
  {
    manufacturer: 'Hikvision',
    resolution: '3840x2160',
    imagers: 4,
    basis: 'datasheet',
    source: 'https://www.hikvision.com/en/products/IP-Products/Network-Cameras/Panoramic-Series/ds-2cd6924g0-ihs--nfc-/ : four 2.8 mm lenses and four sensors stitched to a 180 deg view, 8 MP as 4 x 2 MP',
  },
  {
    manufacturer: 'Dahua',
    resolution: '3840x2160',
    imagers: 4,
    vfovDeg: 80,
    basis: 'datasheet',
    source: 'https://www.dahuasecurity.com/asset/upload/download/dh-ipc-pfw8800-a180_datasheet_20170622.pdf : "Four 1/2.8\" 2Megapixel progressive scan STARVIS CMOS"',
  },
  {
    manufacturer: 'Dahua',
    resolution: '4096x1800',
    imagers: 4,
    vfovDeg: 80,
    basis: 'datasheet',
    source: 'https://www.dahuasecurity.com/asset/upload/download/dh-ipc-pfw8800-a180_datasheet_20170622.pdf : four 2 MP sensors, max 25 fps at 4096x1800',
  },
  {
    manufacturer: 'i-PRO',
    resolution: '3840x2160',
    imagers: 4,
    basis: 'datasheet',
    source: 'https://i-pro.com/global/en/surveillance/products/wv-s8530n : four repositionable lenses, 4 x FHD for 8 MP total',
  },
  {
    manufacturer: 'Reolink',
    resolution: '4608x1728',
    basis: 'resolution',
    imagers: 2,
    source: '4608x1728 = 2 x (2304x1728), two 4 MP imagers; the family is named Duo',
  },
  {
    manufacturer: 'Reolink',
    resolution: '5120x1440',
    basis: 'resolution',
    imagers: 2,
    source: '5120x1440 = 2 x (2560x1440), two 4 MP imagers side by side',
  },
  {
    manufacturer: 'Arecont Vision',
    resolution: '6400x1200',
    basis: 'resolution',
    imagers: 4,
    source: '6400x1200 = 4 x (1600x1200); AV8 in the model name is the 8 MP total, i.e. 4 x 2 MP',
  },
  {
    manufacturer: 'Arecont Vision',
    resolution: '8192x1536',
    basis: 'resolution',
    imagers: 4,
    source: '8192x1536 = 4 x (2048x1536); AV12 in the model name is the 12 MP total, i.e. 4 x 3 MP',
  },
  {
    manufacturer: 'Arecont Vision',
    resolution: '10240x1920',
    basis: 'resolution',
    imagers: 4,
    source: '10240x1920 = 4 x (2560x1920); AV20 in the model name is the 20 MP total, i.e. 4 x 5 MP',
  },
];

/** Verified or reconstructed imager count for a catalog model, by exact name
 *  first and then by family rule. Returns undefined when neither knows. */
export function getPanoramicImagers(
  name?: string,
  manufacturer?: string,
  resolution?: string,
  focalLength?: string,
): PanoramicImagerSpec | undefined {
  if (name && PANORAMIC_IMAGERS[name]) return PANORAMIC_IMAGERS[name];
  // The manufacturer's own "4x2.8mm" beats any reconstruction of ours. The
  // guard is on the STRING rather than on the parse result so the message
  // below cannot print the word "undefined" at a user.
  if (focalLength) {
    const fromFocal = imagersFromFocalLength(focalLength);
    if (fromFocal !== null) {
      return {
        imagers: fromFocal,
        basis: 'focal',
        source: `the catalog focal length reads "${focalLength}", i.e. ${fromFocal} imagers`,
      };
    }
  }
  if (!manufacturer) return undefined;
  if (name) {
    const byName = PANORAMIC_IMAGER_NAME_RULES.find(
      (r) => r.manufacturer === manufacturer && r.pattern.test(name),
    );
    if (byName) return { imagers: byName.imagers, basis: byName.basis, source: byName.source };
  }
  if (!resolution) return undefined;
  const rule = PANORAMIC_IMAGER_RULES.find(
    (r) => r.manufacturer === manufacturer && r.resolution === resolution,
  );
  return rule
    ? {
      imagers: rule.imagers, vfovDeg: rule.vfovDeg, rows: rule.rows, rollCarried: rule.rollCarried,
      frameSpanDeg: rule.frameSpanDeg, rowBend: rule.rowBend, flankScale: rule.flankScale,
      basis: rule.basis, source: rule.source,
    }
    : undefined;
}

/**
 * How many imagers a panoramic body is modelled as.
 *
 * DERIVED, and deliberately visible as such. Our data does not carry the
 * count: `multiSensorIndex` covers 111 multi-directional and dual bodies and
 * carries `channelCount` for those, but it EXCLUDES stitched panoramas by
 * design, which are exactly the bodies that reach this function.
 *
 * The rule is the one physical constraint that holds across the class: a flat
 * imager behind a rectilinear lens stops being practical somewhere near 90
 * degrees, which is why no manufacturer builds a wider one and why every
 * stitched panorama is a fan. Real devices bracket the result rather than
 * matching it exactly (Vivotek MS9390-HV and Hanwha PNM-C9022RV split 180 deg
 * across 2, Hanwha PNM-9030V across 3, Axis P3807-PVE across 4), so the count
 * is a floor on the seam structure, not a claim about a specific SKU.
 *
 * Erring low is the safe direction: fewer, wider imagers push each near edge
 * FURTHER out at its flanks, so the blind area under the camera comes out
 * larger. A count that is too high would hand back coverage the device does
 * not have.
 */
export function defaultImagerCount(spanDeg: number): number {
  if (!Number.isFinite(spanDeg) || spanDeg <= 0) return 1;
  return Math.max(2, Math.ceil(spanDeg / 90));
}

/**
 * How far past its own slice each imager of a stitched panorama actually sees,
 * per side, in degrees.
 *
 * Not a fudge: a stitched panorama CANNOT work without it. The stitcher needs
 * common scene content in both frames to register them, so every such device
 * ships imagers whose fields overlap, typically by something in the 5 to 15
 * percent range of each imager's width.
 *
 * It matters to what we draw because two adjacent frames modelled edge to edge
 * meet exactly on one line and nowhere else: each imager carries its own "up"
 * axis, rotated with it about the body, so above and below the optical axis the
 * two flat frames lean apart and the gap grows with distance. Reported from the
 * 3D view 2026-08-13, "tam dalej sie rozjezdza, a obraz musi sie pokrywac".
 *
 * It deliberately does NOT change the ground footprint. At a seam BOTH imagers
 * are looking along their own flank, which is where each reaches least far in;
 * letting one see a little past the seam only adds ground at an even wider
 * angle than the owner already covers, so the dead wedge under the camera is
 * unchanged. Overlap closes a rendering seam, it does not buy coverage.
 *
 * Expressed as a FRACTION of the imager's own slice rather than a fixed angle,
 * so it stays the same proportion whatever the count: a fixed 6 degrees is 13
 * percent of a two-imager body's 90 degree slice but 27 percent of a
 * four-imager body's 45 degree one, and only one of those can be typical.
 */
export const IMAGER_OVERLAP_FRACTION = 0.07;

/** This imager's rendered field: its owned slice widened on both sides. */
export function imagerRenderSpanDeg(ownedSpanDeg: number): number {
  if (!Number.isFinite(ownedSpanDeg) || ownedSpanDeg <= 0) return 0;
  return Math.min(ownedSpanDeg * (1 + 2 * IMAGER_OVERLAP_FRACTION), 170);
}

/**
 * How far a panoramic body on a wall holds its lens out from the masonry,
 * metres, where the maker documents the wall bracket it goes on.
 *
 * A panorama has no drawn body (cameraBodySpec), so without this its lens sat
 * a millimetre off the facade, and a 180 degree camera on a hall corner showed
 * its own wall where the real one sees the paving past the corner (Dahua
 * PDW3849, owner 2026-10-07). Taken to the camera's axis on the bracket, from
 * the bracket's datasheet; the lens's own few centimetres forward in the ball
 * are not published and are left out rather than guessed.
 */
export interface PanoramicWallMount {
  manufacturer: string;
  pattern: RegExp;
  lensOffWallM: number;
  source: string;
}

export const PANORAMIC_WALL_MOUNTS: readonly PanoramicWallMount[] = [
  {
    // The camera's datasheet lists its wall mount as PFB205W-E. That bracket
    // is 161.6 x 125.6 mm, and the camera's plate is centred in its round end,
    // half the width back from the tip: 161.6 - 125.6 / 2 = 98.8 mm.
    manufacturer: 'Dahua',
    pattern: /^(DH-)?IPC-PDW3849-A180/,
    lensOffWallM: 0.0988,
    source: 'IPC-PDW3849-A180-AS-PV_datasheet_20230821.pdf: "PFB205W-E Wall Mount Bracket", "Φ122 mm × 135.2 mm"; Dahua PFB205W datasheet Rev 001.001: "161.6 mm × 125.6 mm × 76.0 mm"',
  },
];

/** The wall standoff above for this model, or undefined when none is documented. */
export function panoramicLensOffWallM(model?: string, manufacturer?: string): number | undefined {
  if (!model) return undefined;
  return PANORAMIC_WALL_MOUNTS.find((m) => (!manufacturer || m.manufacturer === manufacturer) && m.pattern.test(model))?.lensOffWallM;
}
